<template>
  <transition name="modal">
    <div v-if="visible" class="modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container notifyme-modal">
          <div class="notifyme-modal-header">
            <input
              id="notification-status"
              type="checkbox"
              class="main-checkbox"
              :checked="notifyMeChecked"
              @change="toggleNotifyMeChecked"
            />
            <label class="main-label" for="notification-status">
              Be the first to know when
              {{ productId ? 'this wine or other ' : '' }}offerings from this
              producer become available.
            </label>
          </div>
          <FormKit
            type="form"
            :actions="false"
            :form-errors="errorTexts"
            name="notify_me_form"
            @submit="submit"
          >
            <div class="notify-options">
              <div class="row">
                <div class="columns">
                  <label for="notifyme-sms">
                    <img
                      src="../../../wineaccess/assets/images/sms-black.svg"
                      role="presentation"
                      alt="Speech bubble"
                    />
                    Notify me by SMS
                  </label>
                  <FormKit
                    id="notifyme-sms"
                    type="tel"
                    name="phone"
                    :value="notificationPhone"
                    placeholder="Phone Number"
                    aria-label="SMS Telephone Number"
                    :errors="phoneErrors"
                    :validation-rules="{ validPhoneNumber }"
                    validation="validPhoneNumber"
                    :validation-messages="{
                      validPhoneNumber:
                        'Error: Phone number is incorrect, please use this format: (XXX)-XXX-XXXX',
                    }"
                    @input="(event) => (notificationPhone = event)"
                  />
                  <div v-if="phoneErrors" class="error-messages">
                    <div
                      v-for="error in phoneErrors"
                      :key="error"
                      class="error-message"
                    >
                      • {{ error }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="columns">
                  <label for="notifyme-email">
                    <img
                      src="../../../wineaccess/assets/images/email-black.svg"
                      role="presentation"
                      alt="At Symbol"
                    />
                    Notify me by Email
                  </label>
                  <FormKit
                    id="notifyme-email"
                    type="email"
                    name="email"
                    :value="notificationEmail"
                    placeholder="Email Address"
                    aria-label="Email Address"
                    :errors="emailErrors"
                    validation="bail|required:trim|email"
                    @input="(event) => (notificationEmail = event)"
                  />
                  <div v-if="emailErrors" class="error-messages">
                    <div
                      v-for="error in emailErrors"
                      :key="error"
                      class="error-message"
                    >
                      • {{ error }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <FormKit
              type="submit"
              :disabled="submitting"
              label="Save"
              input-class="primary"
            />
          </FormKit>
          <button class="button-link" @click="close">Cancel</button>
          <p class="disclaimer">
            By selecting SMS, I agree to receive SMS text messages about this
            producer from Wine Access. Carrier rates may apply.
          </p>
          <button class="close" @click="close()">Close</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { fullURL } from '#common/utils/url'
import { validatePhoneNumber } from '#common/utils/phone'
const route = useRoute()
const { $auth, $axios } = useNuxtApp()
const userListStore = useUserList()

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  productId: {
    type: Number,
    default: null,
    required: false,
  },
  wineryId: {
    type: Number,
    default: null,
    required: false,
  },
})

const emit = defineEmits(['closed', 'submitted'])

const validPhoneNumber = ({ value }) => validatePhoneNumber(value, true)

// Initialize notification data
let initialEmail = $auth.user.email
let initialNotification = null

if (props.productId) {
  initialNotification = $auth.user.notifyme_products[props.productId]
}
if (props.wineryId) {
  initialNotification = $auth.user.notifyme_wineries[props.wineryId]
}

if (initialNotification) {
  initialEmail = initialNotification.notification_email || $auth.user.email
}

// Reactive state
const submitting = ref(false)
const submitErrors = ref({})
const notifyMeChecked = ref(true)
const notificationEmail = ref(initialEmail)
const notificationPhone = ref($auth.user.notification_phone_number)

// Computed properties
const notification = computed(() => {
  if (props.productId) {
    return $auth.user.notifyme_products[props.productId]
  }
  if (props.wineryId) {
    return $auth.user.notifyme_wineries[props.wineryId]
  }
  return null
})

const errorTexts = computed(() => {
  return Object.values(submitErrors.value).flat()
})

const phoneErrors = computed(() => {
  return submitErrors.value.notification_phone
})

const emailErrors = computed(() => {
  return submitErrors.value.notification_email
})

const loggedIn = computed(() => {
  return $auth.loggedIn
})

// Methods
const toggleNotifyMeChecked = () => {
  notifyMeChecked.value = !notifyMeChecked.value
}

watch(loggedIn, (newValue, oldValue) => {
  if (newValue) {
    notificationEmail.value = $auth.user.email
    notificationPhone.value = $auth.user.notification_phone_number
  }
})

const close = () => {
  emit('closed')
}

const submit = async () => {
  submitting.value = true
  submitErrors.value = {}
  let result = null

  try {
    if (notification.value && notification.value.id) {
      const data = {
        notification_email: notificationEmail.value,
        notification_phone: notificationPhone.value,
        is_active: notifyMeChecked.value,
        referrer_url: fullURL(route),
      }
      if (props.productId) data.product = props.productId
      if (props.wineryId) data.winery = props.wineryId
      result = await $axios.patch(
        `/api/notifyme/notification-subscription/${notification.value.id}/`,
        data,
      )
    } else if (notifyMeChecked.value) {
      const data = {
        notification_email: notificationEmail.value,
        notification_phone: notificationPhone.value,
        referrer_url: fullURL(route),
      }
      if (props.productId) data.product = props.productId
      if (props.wineryId) data.winery = props.wineryId
      // Only create a notification if it will be active
      result = await $axios.post(
        '/api/notifyme/notification-subscription/',
        data,
      )
    }
    if (result) {
      if (props.productId) {
        userListStore.updateSubscription($auth.user, result.data)
      }
      if (result.data.notification_phone) {
        $auth.user.notification_phone_number = result.data.notification_phone
      }
      if (props.wineryId) {
        $auth.user.notifyme_wineries[props.wineryId] = result.data
      }
      emit('submitted', result.data)
    }

    close()
    submitting.value = false
  } catch (error) {
    Sentry.captureException(error)
    submitErrors.value = error.response
      ? error.response.data
      : { error: ['Error saving notification information'] }
    submitting.value = false
  }
}
</script>
